import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { UploadCloud, FileUp, Copy, Check, Loader2, CheckCircle2, AlertCircle, FileSpreadsheet, Package, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';

const enconOrange = '#ee7203';
const UPLOAD_EMAIL = 'orders@c30dashboard.uk';

const RESULT_CONFIG = {
  daily_order_entry:  { label: 'Daily Order Entry Report', icon: FileSpreadsheet, color: '#2563eb' },
  order_book_summary: { label: 'Order Book Summary',       icon: Package,        color: '#16a34a' },
  free_stock_report:  { label: 'Free Stock Report',        icon: FileSpreadsheet, color: '#7c3aed' },
  order_pdf:          { label: 'Order / Quote PDF',        icon: FileUp,          color: enconOrange },
  ho_support_monthly: { label: 'H/O Support Actuals',      icon: Package,        color: '#0891b2' },
};

/**
 * GlobalUploadModal — a single drag-and-drop / browse upload surface that
 * auto-detects the file type (Daily Order Entry Report, Order Book Summary,
 * Free Stock Report, or an order/quote PDF) and routes it to the right place.
 * Also includes the M4 Sales Data screenshot-paste flow inline (one button,
 * one modal for every upload — not a separate secondary modal) for users
 * with Sales Tracker upload permission.
 *
 * Optional onSelectTransportCSV prop keeps the older dedicated Transport CSV
 * modal reachable from pages that still render it (e.g. Hub.jsx) — it renders
 * as a small secondary link and is omitted entirely when not supplied.
 */
const GlobalUploadModal = ({ isOpen, onClose, onSelectTransportCSV, user }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const canUploadM4 = user?.role === 'super_admin' || user?.can_upload_sales_tracker;
  const [uploadDate, setUploadDate] = useState('');
  const [uploadingM4, setUploadingM4] = useState(false);

  useEffect(() => {
    if (isOpen && canUploadM4 && !uploadDate) {
      const today = new Date();
      let previousDay = new Date(today);
      previousDay.setDate(previousDay.getDate() - 1);
      while (previousDay.getDay() === 0 || previousDay.getDay() === 6) previousDay.setDate(previousDay.getDate() - 1);
      setUploadDate(previousDay.toISOString().split('T')[0]);
    }
  }, [isOpen, canUploadM4, uploadDate]);

  const reset = () => { setResult(null); setUploading(false); setDragOver(false); };
  const handleClose = () => { reset(); onClose(); };

  const copyEmail = () => {
    navigator.clipboard.writeText(UPLOAD_EMAIL);
    setCopied(true);
    toast.success('Email address copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(`${API}/order-book-reports/smart-upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult({ ok: true, ...res.data, filename: file.name });
      toast.success(res.data?.message || 'File uploaded successfully');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed — file type not recognised';
      setResult({ ok: false, message: detail, filename: file.name });
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleM4PhotoUpload = useCallback(async (file) => {
    if (!file || !uploadDate) { toast.error('Please select a date first'); return; }
    setUploadingM4(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const fyResponse = await api.get(`${API}/financial-years`);
      const financialYears = fyResponse.data.financial_years || fyResponse.data;
      const currentYear = financialYears.find(fy => fy.is_current) || financialYears[0];
      if (!currentYear) { toast.error('No financial year found. Please set up a financial year first.'); setUploadingM4(false); return; }
      await api.post(`${API}/sales-tracker/upload-photo`, formData, {
        params: { year_id: currentYear.id, date: uploadDate },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('M4 data uploaded and analyzed successfully!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload M4 data. Please try again.');
    } finally {
      setUploadingM4(false);
    }
  }, [uploadDate]);

  const handleM4PasteImage = (e) => {
    e.preventDefault();
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        handleM4PhotoUpload(items[i].getAsFile());
        break;
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="global-upload-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2" style={{ color: enconOrange }}>
            <UploadCloud className="w-7 h-7" />
            Upload Data
          </DialogTitle>
          <DialogDescription>
            Drop any report and we'll detect what it is and send it to the right place.
          </DialogDescription>
        </DialogHeader>

        {/* Email address — forward reports here instead if preferred */}
        <button
          onClick={copyEmail}
          data-testid="global-upload-copy-email"
          className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border text-sm transition-colors hover:bg-orange-50"
          style={{ borderColor: `${enconOrange}55`, background: `${enconOrange}0d` }}
        >
          <span className="text-gray-600 text-left">Prefer email? Send it to your own @encon.co.uk email, then forward that to:</span>
          <span className="flex items-center gap-1.5 font-semibold whitespace-nowrap" style={{ color: enconOrange }}>
            {UPLOAD_EMAIL}
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          </span>
        </button>

        {/* Drag & drop / browse zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="global-upload-dropzone"
          className="mt-2 flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-colors"
          style={{
            borderColor: dragOver ? enconOrange : '#d1d5db',
            background: dragOver ? `${enconOrange}0d` : '#fafafa',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="global-upload-file-input"
          />
          {uploading ? (
            <>
              <Loader2 className="w-9 h-9 mb-2 animate-spin" style={{ color: enconOrange }} />
              <p className="text-sm font-medium text-gray-500">Detecting &amp; uploading…</p>
            </>
          ) : (
            <>
              <UploadCloud className="w-9 h-9 mb-2" style={{ color: enconOrange }} />
              <p className="text-sm text-gray-600"><span className="font-semibold">Click to browse</span> or drag &amp; drop</p>
              <p className="text-xs text-gray-400 mt-1">PDF orders/quotes · Order Book reports · Free Stock report</p>
            </>
          )}
        </div>

        {/* Result banner */}
        {result && (
          <div
            data-testid="global-upload-result"
            className={`flex items-start gap-3 p-3 rounded-xl border text-sm ${result.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
          >
            {result.ok ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            )}
            <div>
              {result.ok && result.detected_type && RESULT_CONFIG[result.detected_type] && (
                <p className="font-semibold text-gray-800 flex items-center gap-1.5">
                  {(() => { const Icon = RESULT_CONFIG[result.detected_type].icon; return <Icon className="w-3.5 h-3.5" style={{ color: RESULT_CONFIG[result.detected_type].color }} />; })()}
                  Detected: {RESULT_CONFIG[result.detected_type].label}
                </p>
              )}
              <p className={result.ok ? 'text-green-700' : 'text-red-600'}>{result.message}</p>
            </div>
          </div>
        )}

        {/* M4 Sales Data screenshot — same modal, same button, built-in (permission gated) */}
        {canUploadM4 && (
          <div className="border-t pt-4 space-y-3" data-testid="global-upload-m4-section">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: enconOrange }} />
              <span className="font-semibold text-sm text-gray-700">Or paste an M4 Sales screenshot</span>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Date (defaults to previous working day)</Label>
              <Input
                type="date"
                value={uploadDate}
                data-testid="global-upload-m4-date"
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value + 'T00:00:00');
                  const dayOfWeek = selectedDate.getDay();
                  if (dayOfWeek === 0 || dayOfWeek === 6) { toast.error('Weekends cannot be selected. Please choose a weekday.'); return; }
                  setUploadDate(e.target.value);
                }}
              />
            </div>
            <div
              contentEditable
              suppressContentEditableWarning
              onPaste={handleM4PasteImage}
              data-testid="global-upload-m4-paste-zone"
              className="border-2 border-dashed border-blue-300 rounded-xl p-3 min-h-[64px] bg-blue-50 cursor-text flex items-center justify-center text-center"
            >
              <p className="text-blue-600 text-xs">Click here and paste (Ctrl+V / Cmd+V) or browse a file below</p>
            </div>
            <Input
              type="file"
              accept="image/*"
              data-testid="global-upload-m4-file-input"
              onChange={(e) => { handleM4PhotoUpload(e.target.files?.[0]); e.target.value = ''; }}
              disabled={uploadingM4}
            />
            {uploadingM4 && (
              <div className="flex items-center gap-2 text-sm text-blue-600" data-testid="global-upload-m4-analyzing">
                <Loader2 className="w-4 h-4 animate-spin" /> Analyzing image with AI…
              </div>
            )}
          </div>
        )}

        {/* Secondary legacy entry point — only shown if the parent page still supports it */}
        {onSelectTransportCSV && (
          <div className="flex items-center gap-3 justify-center text-xs text-gray-400 pt-1">
            <span>Prefer a manual flow?</span>
            <button
              onClick={() => { onSelectTransportCSV(); handleClose(); }}
              data-testid="global-upload-legacy-transport"
              className="underline hover:text-gray-600"
            >
              Transport CSV
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalUploadModal;
